import * as z from 'zod';

import { TraceIdSchema } from './primitives.js';

/**
 * The canonical error envelope (architecture §4). Every non-2xx response from
 * the API has exactly this body; `AllDomainErrorsFilter` on the server and
 * `ApiError` in the client are the two ends of this single contract.
 */

export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'ALREADY_EXISTS',
  'CONFLICT',
  /** Optimistic concurrency: `expectedRevision` did not match the aggregate. */
  'REVISION_CONFLICT',
  /** Soft element lock held by another author. */
  'ELEMENT_LOCKED',
  /** Command is illegal for the aggregate's current lifecycle state. */
  'INVALID_STATE',
  'IMMUTABLE',
  'ONTOLOGY_INVALID',
  /**
   * An aggregate refused to enter an inconsistent state. Broader than
   * `ONTOLOGY_INVALID`, which means specifically "the graph breaks an ontology
   * rule": this is any domain invariant, and it is not a schema failure, so
   * `VALIDATION_FAILED` would send the client looking at its own request.
   */
  'INVARIANT_VIOLATION',
  'IMPORT_FAILED',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'RATE_LIMITED',
  'ASSIST_UNAVAILABLE',
  'ASSIST_CANCELLED',
  'ASSIST_BUDGET_EXCEEDED',
  'DEPENDENCY_UNAVAILABLE',
  /**
   * A stored credential could not be opened: AES-GCM authentication failed, or
   * the row was sealed with a key version the server no longer holds. Distinct
   * from `INTERNAL` because it is actionable — the owner must re-enter the
   * credential in the admin panel. Carries no plaintext, ciphertext or key
   * material, and the message never quotes a credential value.
   */
  'CREDENTIAL_INTEGRITY_FAILURE',
  'INTERNAL',
  /** Client-side only: the transport failed before a response arrived. */
  'NETWORK',
  /** Client-side only: the caller aborted via `AbortSignal`. */
  'REQUEST_ABORTED',
  /** Client-side only: a response did not match the contract schema. */
  'CONTRACT_VIOLATION',
] as const;
export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

export const ErrorBodySchema = z.strictObject({
  code: ErrorCodeSchema,
  message: z.string().min(1).max(2_000),
  /** Machine-readable specifics: zod issue list, conflicting revision, lock holder. */
  details: z.unknown().optional(),
  traceId: TraceIdSchema,
});
export type ErrorBody = z.infer<typeof ErrorBodySchema>;

export const ErrorEnvelopeSchema = z.strictObject({ error: ErrorBodySchema });
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

/**
 * Code → HTTP status. Owned here so the API's exception filter, the OpenAPI
 * document and the client's status mapping cannot drift apart. Client-only
 * codes map to 0 — they never travel over the wire.
 */
export const ERROR_STATUS: Readonly<Record<ErrorCode, number>> = {
  VALIDATION_FAILED: 400,
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  ALREADY_EXISTS: 409,
  CONFLICT: 409,
  REVISION_CONFLICT: 409,
  ELEMENT_LOCKED: 409,
  INVALID_STATE: 409,
  IMMUTABLE: 409,
  ONTOLOGY_INVALID: 422,
  INVARIANT_VIOLATION: 422,
  IMPORT_FAILED: 422,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RATE_LIMITED: 429,
  ASSIST_UNAVAILABLE: 503,
  ASSIST_CANCELLED: 499,
  ASSIST_BUDGET_EXCEEDED: 429,
  DEPENDENCY_UNAVAILABLE: 503,
  CREDENTIAL_INTEGRITY_FAILURE: 500,
  INTERNAL: 500,
  NETWORK: 0,
  REQUEST_ABORTED: 0,
  CONTRACT_VIOLATION: 0,
};

/** Statuses the API may return for any authenticated route, for OpenAPI. */
export const COMMON_ERROR_STATUSES = [400, 401, 403, 500] as const;
